'use client';

import '../styles/globals.css';

import ErrorPage from '@components/ErrorPage';
import { resolveLocale } from '@i18n/config';
import { NextIntlClientProvider } from 'next-intl';
import { useEffect, useState } from 'react';
import { GLOBAL_ERROR_MESSAGES } from './global-error-messages';

/**
 * Replaces the root layout when it fails, so it renders its own document,
 * styles, theme and (inlined) error strings.
 */
export default function GlobalError({
  error,
  retry
}: {
  error: Error & { digest?: string };
  retry: () => void;
}) {
  // The locale cookie is httpOnly; the failed layout's <html lang> carries
  // it when the error happens in the browser.
  const [locale] = useState(() =>
    resolveLocale(
      typeof document === 'undefined'
        ? undefined
        : document.documentElement.lang
    )
  );
  // Dark is the app default; next-themes keeps the choice in localStorage.
  const [dark, setDark] = useState(true);

  useEffect(() => {
    console.error('Error in root layout:', error);
  }, [error]);

  useEffect(() => {
    try {
      const theme = localStorage.getItem('theme');
      setDark(
        theme === 'light'
          ? false
          : theme === 'system'
            ? matchMedia('(prefers-color-scheme: dark)').matches
            : true
      );
    } catch {
      // Storage unavailable: keep the default.
    }
  }, []);

  return (
    <html lang={locale} className={dark ? 'dark' : undefined}>
      <body className="h-full w-full bg-background font-normal text-body text-foreground">
        <NextIntlClientProvider
          locale={locale}
          messages={GLOBAL_ERROR_MESSAGES[locale]}
        >
          <main className="flex min-h-dvh w-full flex-col justify-center p-5">
            <ErrorPage reset={retry} />
          </main>
        </NextIntlClientProvider>
      </body>
    </html>
  );
}
